import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import * as Yup from 'yup';

import withErrorContainer from '@zr/components/withErrorContainer';
import { NAME, PHONE_NUMBER, MOBILE, RESUME, RESUME_NAME } from '@zr/utilities/profileFields';

import {
  STRING_SCREEN_APPLY_NOW,
  STRING_SCREEN_CONTINUE,
} from '@zr/jobseeker-public/apps/jobseeker-screens/labels';

import {
  STRING_FIELD_PHONE_NUMBER_REQUIRED,
  STRING_FIELD_RESUME_REQUIRED,
} from '@zr/components/Fields/labels';

import FullNameField from '@zr/components/Fields/Formik/FullNameField';
import PhoneNumberField from '@zr/components/Fields/Formik/PhoneNumberField';
import ApplyResumeUpload from '@zr/components/Fields/Formik/ApplyResumeUpload';
import NoResumeField from '@zr/components/Fields/Formik/NoResumeField';

import { contactPropTypes } from '@zr/jobseeker-public/apps/jobseeker-screens/components/screenTypeSchema';
import { FirstNameLastNameValidation } from '@jobseeker/public/apps/jobseeker-screens/validation';

import {
  USE_RESUME_VALIDATION,
  NO_RESUME,
  handleResumeFormSubmit,
} from './resumeUtilities';
import { combineStrings } from './utilities';

import './ScreenViewWrapper.less';
import './ContactPostParse.less';

const USE_ALREADY_UPLOADED_RESUME = 'use_already_uploaded_resume';

const defaultStrings = {
  title: STRING_SCREEN_APPLY_NOW,
  cta: STRING_SCREEN_CONTINUE,
};

// eslint-disable-next-line arrow-body-style
const validationSchema = () => Yup.lazy((values) => {
  return Yup.object({
    [NAME]: FirstNameLastNameValidation,
    [PHONE_NUMBER]: Yup.string().trim().required(STRING_FIELD_PHONE_NUMBER_REQUIRED),
    // they need to either upload resume or state they dont have one
    [RESUME]: Yup.string().test(
      'resume_required',
      STRING_FIELD_RESUME_REQUIRED,
      // we don't need to upload the same resume again if we just did that
      // when the form submits, we won't upload a resume if values[RESUME] is empty
      () => (values[NO_RESUME] || values[RESUME] || values[USE_ALREADY_UPLOADED_RESUME]),
    ),
  });
});

export function ContactPostParse({
  disabled, onNoResumeContinue, onResumeContinue, onError, onErrorClose, initialContact, setIsLoading,
  parseFirstResult: {
    // aliasing to make variable names more obvious
    errorMessage: parseErrorMessage,
    fileName: parseFileName,
    resumeJso: parseResumeJso,
    // resume data if we need to submit again
    resume,
    uploadedResume,
  },
  // this row of props is just for telemetry
  method, onParsedNameChange, onParsedPhoneNumberChange, strings,
}) {
  const labels = useMemo(() => combineStrings(strings, defaultStrings), [strings]);

  const handleFormSubmit = handleResumeFormSubmit({
    alreadyUploadedResume: uploadedResume,
    method,
    onError,
    onNoResumeSuccess: onNoResumeContinue,
    onResumeSuccess: onResumeContinue,
    onStart: onErrorClose,
    setIsLoading,
  });

  const initialFormValues = {
    // prioritize profile over resume values still
    // parseResumeJso might not have NAME or PHONE_NUMBER set
    [NAME]: initialContact[NAME] || parseResumeJso?.[NAME] || '',
    // note: resume parsing seems to only put parsed phone numbers in 'mobile'......
    [PHONE_NUMBER]: initialContact[PHONE_NUMBER] || parseResumeJso?.[PHONE_NUMBER] || parseResumeJso?.[MOBILE] || '',
    [RESUME]: resume || '',
    [RESUME_NAME]: parseFileName || '',
    [NO_RESUME]: false,
    // relax resume validation if we try to submit the same resume again
    [USE_RESUME_VALIDATION]: !resume,
    [USE_ALREADY_UPLOADED_RESUME]: uploadedResume,
  };

  // initialize form with any upload/parse error from the previous screen
  const initialErrors = parseErrorMessage ? {
    [RESUME]: parseErrorMessage,
  } : null;

  // touched to show the resume error
  const initialTouched = parseErrorMessage ? {
    [RESUME]: true,
  } : null;

  // did the job seeker change the parsed name and/or phone number? for telemetry
  const usingParsedName = !initialFormValues[NAME] && parseResumeJso?.[NAME];
  const usingParsedPhoneNumber = !initialFormValues[PHONE_NUMBER] && (
    parseResumeJso?.[PHONE_NUMBER] || parseResumeJso?.[MOBILE]
  );
  let changedParsedName = false;
  let changedParsedPhoneNumber = false;
  let onNameChange = () => {};
  if (usingParsedName) {
    onNameChange = () => {
      // only check if they changed it once
      if (!changedParsedName) {
        changedParsedName = true;
        onParsedNameChange();
      }
    };
  }
  let onPhoneNumberChange = () => {};
  if (usingParsedPhoneNumber) {
    onPhoneNumberChange = () => {
      // only check if they changed it once
      if (!changedParsedPhoneNumber) {
        changedParsedPhoneNumber = true;
        onParsedPhoneNumberChange();
      }
    };
  }

  return (
    <Formik
      initialErrors={initialErrors}
      initialTouched={initialTouched}
      initialValues={initialFormValues}
      onSubmit={handleFormSubmit}
      validationSchema={validationSchema}
    >
      {(formikProps) => {
        const {
          handleSubmit, isSubmitting, setValues, values,
        } = formikProps;
        // this logic disables the submit button to convey to the user they need to select their resume or confirm they have no resume
        // this is different from form validness: that can include invalid input like resumes (which they can submit again for laxer validation)
        const areRequiredFieldsFilled = values[NAME] && values[PHONE_NUMBER] && (
          values[RESUME] || values[NO_RESUME] || values[USE_ALREADY_UPLOADED_RESUME]
        );

        const unsetResume = () => {
          setValues({
            ...values,
            [RESUME]: '',
            [RESUME_NAME]: '',
            // if they remove their resume, then don't care about the resume they just uploaded
            [USE_ALREADY_UPLOADED_RESUME]: false,
          });
        };

        const handleNoResumeChange = (e) => {
          // if they say they have no resume, remove a picked resume if they did that
          if (e.target.checked) {
            unsetResume();
          }
        };

        const handleFileAdd = (inputValues) => {
          setValues({
            ...values,
            // inputValues has RESUME and RESUME_NAME
            ...inputValues,
            // if they added a file, ensure this checkbox is unchecked
            [NO_RESUME]: false,
            // if they change their resume, ensure we re-validate
            [USE_RESUME_VALIDATION]: true,
            // if they change their resume, then don't care about the resume they just uploaded
            [USE_ALREADY_UPLOADED_RESUME]: false,
          });
        };

        return (
          <form className="ScreenViewWrapper ContactPostParse" onSubmit={handleSubmit}>
            <fieldset disabled={disabled}>
              <legend className="title">{labels.title}</legend>
              <FullNameField
                name={NAME}
                onChange={onNameChange}
                formikProps={formikProps}
              />
              <PhoneNumberField
                name={PHONE_NUMBER}
                onChange={onPhoneNumberChange}
                formikProps={formikProps}
              />
              <ApplyResumeUpload
                formikProps={formikProps}
                onFileAdd={handleFileAdd}
                onFileRemove={unsetResume}
              />
              {/* do not show no resume checkbox if they picked a resume or using the one that was just uploaded */}
              {!(values[RESUME] || values[USE_ALREADY_UPLOADED_RESUME]) && (
                <NoResumeField
                  name={NO_RESUME}
                  onChange={handleNoResumeChange}
                  formikProps={formikProps}
                />
              )}
              <div className="button_wrapper">
                <button
                  className="primary_button"
                  disabled={!areRequiredFieldsFilled || isSubmitting || disabled}
                  type="submit"
                >
                  {labels.cta}
                </button>
              </div>
            </fieldset>
          </form>
        );
      }}
    </Formik>
  );
}

ContactPostParse.propTypes = {
  disabled: PropTypes.bool,
  method: PropTypes.string,
  onError: PropTypes.func.isRequired,
  onErrorClose: PropTypes.func.isRequired,
  onNoResumeContinue: PropTypes.func.isRequired,
  onParsedNameChange: PropTypes.func,
  onParsedPhoneNumberChange: PropTypes.func,
  onResumeContinue: PropTypes.func.isRequired,
  parseFirstResult: PropTypes.shape({
    errorMessage: PropTypes.string,
    fileName: PropTypes.string,
    resumeJso: PropTypes.shape({}),
    resume: PropTypes.string,
    uploadedResume: PropTypes.bool,
  }).isRequired,
  initialContact: contactPropTypes.isRequired,
  setIsLoading: PropTypes.func,
  strings: PropTypes.object,
};

ContactPostParse.defaultProps = {
  disabled: false,
  method: null,
  onParsedNameChange: () => {},
  onParsedPhoneNumberChange: () => {},
  setIsLoading: () => {},
  strings: undefined,
};

export default withErrorContainer(ContactPostParse);
